/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
/**
 * cryptoLocal.js — Primitive crittografiche Web Crypto per SanitaNexus.
 *
 * Tutto gira NEL BROWSER: il server non vede mai chiavi private o plaintext.
 *
 *  • AES-GCM 256      → cifratura simmetrica di PDF/documenti (FEA, referti)
 *  • PBKDF2           → derivazione chiave da passphrase (310k iterazioni, SHA-256)
 *  • RSA-OAEP 2048    → cifratura ibrida della chiave AES con la chiave pubblica
 *                       del paziente (portale E2EE)
 *  • SHA-256          → impronta documenti (audit / integrità FEA)
 *  • Device key       → chiave AES non estraibile conservata in IndexedDB per
 *                       cifrare i dati locali senza chiedere passphrase
 */
import { getMeta, setMeta } from '@/lib/offlineDb';

const subtle = () => {
  if (typeof crypto === 'undefined' || !crypto.subtle) throw new Error('Web Crypto non disponibile (serve HTTPS)');
  return crypto.subtle;
};

const enc = new TextEncoder();
const dec = new TextDecoder();

// ---------------------------------------------------------------------------
// Encoding helpers
// ---------------------------------------------------------------------------

export const bufToBase64 = (buf) => {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
};

export const base64ToBuf = (b64) => {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
};

export const bufToHex = (buf) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');

export const randomBytes = (n) => crypto.getRandomValues(new Uint8Array(n));

// ---------------------------------------------------------------------------
// Hash
// ---------------------------------------------------------------------------

export const sha256Hex = async (data) => {
  const buf = typeof data === 'string' ? enc.encode(data) : data;
  return bufToHex(await subtle().digest('SHA-256', buf));
};

// ---------------------------------------------------------------------------
// AES-GCM
// ---------------------------------------------------------------------------

export const generateAesKey = (extractable = true) => subtle().generateKey({ name: 'AES-GCM', length: 256 }, extractable, ['encrypt', 'decrypt']);

/** Cifra bytes → { iv (b64), ciphertext (b64) }. */
export const aesEncrypt = async (key, data, aad) => {
  const iv = randomBytes(12);
  const buf = typeof data === 'string' ? enc.encode(data) : data;
  const params = { name: 'AES-GCM', iv, tagLength: 128 };
  if (aad) params.additionalData = enc.encode(aad);
  const ct = await subtle().encrypt(params, key, buf);
  return { iv: bufToBase64(iv), ciphertext: bufToBase64(ct) };
};

/** Decifra { iv, ciphertext } (b64) → ArrayBuffer. */
export const aesDecrypt = async (key, { iv, ciphertext }, aad) => {
  const params = { name: 'AES-GCM', iv: new Uint8Array(base64ToBuf(iv)), tagLength: 128 };
  if (aad) params.additionalData = enc.encode(aad);
  return subtle().decrypt(params, key, base64ToBuf(ciphertext));
};

export const aesDecryptToString = async (key, payload, aad) => dec.decode(await aesDecrypt(key, payload, aad));

// ---------------------------------------------------------------------------
// Passphrase → key (PBKDF2)
// ---------------------------------------------------------------------------

export const PBKDF2_ITERATIONS = 310_000;

export const deriveKeyFromPassphrase = async (passphrase, saltB64, { extractable = false, usages = ['encrypt', 'decrypt'] } = {}) => {
  const salt = saltB64 ? new Uint8Array(base64ToBuf(saltB64)) : randomBytes(16);
  const base = await subtle().importKey('raw', enc.encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  const key = await subtle().deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    extractable,
    usages,
  );
  return { key, salt: bufToBase64(salt) };
};

// ---------------------------------------------------------------------------
// Device key (IndexedDB, non estraibile)
// ---------------------------------------------------------------------------

const DEVICE_KEY_META = 'device_aes_key';

/**
 * Restituisce la chiave AES del dispositivo, creandola al primo uso.
 * La CryptoKey viene salvata in IndexedDB come oggetto strutturato: il
 * materiale della chiave NON è leggibile da JavaScript (extractable=false).
 */
export const getDeviceKey = async () => {
  let key = await getMeta(DEVICE_KEY_META).catch(() => null);
  if (key && key.type === 'secret') return key;
  key = await generateAesKey(false);
  await setMeta(DEVICE_KEY_META, key);
  return key;
};

// ---------------------------------------------------------------------------
// RSA-OAEP (portale paziente E2EE)
// ---------------------------------------------------------------------------

export const generateRsaKeyPair = () => subtle().generateKey(
  { name: 'RSA-OAEP', modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' },
  true,
  ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey'],
);

export const exportPublicKeyB64 = async (publicKey) => bufToBase64(await subtle().exportKey('spki', publicKey));
export const importPublicKeyB64 = (b64) => subtle().importKey('spki', base64ToBuf(b64), { name: 'RSA-OAEP', hash: 'SHA-256' }, true, ['encrypt', 'wrapKey']);

/**
 * Esporta la chiave privata cifrata con passphrase (PBKDF2 + AES-GCM), così il
 * paziente può conservarla/recuperarla senza che il server possa leggerla.
 * → { salt, iv, ciphertext } (tutti b64)
 */
export const exportPrivateKeyEncrypted = async (privateKey, passphrase) => {
  const pkcs8 = await subtle().exportKey('pkcs8', privateKey);
  const { key, salt } = await deriveKeyFromPassphrase(passphrase);
  const payload = await aesEncrypt(key, pkcs8, 'sn-private-key');
  return { salt, ...payload, alg: 'RSA-OAEP-2048/AES-GCM-256/PBKDF2-310k' };
};

export const importPrivateKeyEncrypted = async ({ salt, iv, ciphertext }, passphrase) => {
  const { key } = await deriveKeyFromPassphrase(passphrase, salt);
  const pkcs8 = await aesDecrypt(key, { iv, ciphertext }, 'sn-private-key');
  return subtle().importKey('pkcs8', pkcs8, { name: 'RSA-OAEP', hash: 'SHA-256' }, true, ['decrypt', 'unwrapKey']);
};

/**
 * Cifratura IBRIDA di un documento per un destinatario:
 *   1. chiave AES casuale → cifra il documento (AES-GCM)
 *   2. chiave AES → wrappata con RSA-OAEP (chiave pubblica del paziente)
 * Il server riceve solo: ciphertext + iv + wrappedKey. Illeggibile senza la
 * chiave privata del paziente.
 */
export const hybridEncryptForRecipient = async (publicKeyB64, data, meta = {}) => {
  const pub = await importPublicKeyB64(publicKeyB64);
  const aesKey = await generateAesKey(true);
  const payload = await aesEncrypt(aesKey, data);
  const rawAes = await subtle().exportKey('raw', aesKey);
  const wrapped = await subtle().encrypt({ name: 'RSA-OAEP' }, pub, rawAes);
  return {
    alg: 'AES-GCM-256+RSA-OAEP-2048',
    iv: payload.iv,
    ciphertext: payload.ciphertext,
    wrapped_key: bufToBase64(wrapped),
    sha256: await sha256Hex(data),
    ...meta,
  };
};

export const hybridDecryptWithPrivateKey = async (privateKey, { iv, ciphertext, wrapped_key }) => {
  const rawAes = await subtle().decrypt({ name: 'RSA-OAEP' }, privateKey, base64ToBuf(wrapped_key));
  const aesKey = await subtle().importKey('raw', rawAes, { name: 'AES-GCM' }, false, ['decrypt']);
  return aesDecrypt(aesKey, { iv, ciphertext });
};

/** Scarica un ArrayBuffer come file. */
export const downloadBuffer = (buf, filename, mime = 'application/pdf') => {
  const blob = new Blob([buf], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};
